import { loadingIndicator } from './loading-indicator.js?v=6.91.27';

export class ApiError extends Error {
  constructor(message, {
    status = 0,
    code = 'request_failed',
    details = null,
    action = '',
  } = {}) {
    super(String(message || 'Ошибка запроса.'));
    this.name = 'ApiError';
    this.source = 'api';
    this.status = Number.isInteger(Number(status)) ? Math.max(0, Number(status)) : 0;
    this.code = String(code || 'request_failed');
    this.details = details;
    this.action = String(action || '');
  }
}

export class ApiClient {
  constructor({
    endpoint = 'api.php',
    onAuthenticationLost = () => {},
    onRequestComplete = () => {},
  } = {}) {
    this.endpoint = endpoint;
    this.csrfToken = '';
    this.onAuthenticationLost = onAuthenticationLost;
    this.onRequestComplete = onRequestComplete;
    this.inflight = new Map();
  }

  setCsrfToken(token) {
    this.csrfToken = String(token || '');
  }

  async call(action, input = {}, { signal = null, dedupeKey = '' } = {}) {
    const body = { ...input, action };
    if (this.csrfToken && !Object.prototype.hasOwnProperty.call(body, '_csrf')) {
      body._csrf = this.csrfToken;
    }

    const key = String(dedupeKey || '');
    if (key && this.inflight.has(key)) return this.inflight.get(key);

    const request = this.execute(body, signal).finally(() => {
      if (key) this.inflight.delete(key);
    });
    if (key) this.inflight.set(key, request);
    return request;
  }


  isAuthenticationLost(status, code) {
    const normalizedStatus = Number(status || 0);
    const normalizedCode = String(code || '');
    return normalizedStatus === 401
      || normalizedCode === 'authentication_required'
      || normalizedCode === 'unauthorized'
      || normalizedCode === 'invalid_session'
      || normalizedCode === 'session_idle_timeout'
      || normalizedCode === 'session_revoked'
      || normalizedCode === 'user_blocked';
  }

  async execute(body, signal) {
    const action = String(body.action || '');
    const started = globalThis.performance?.now?.() ?? Date.now();
    let status = 0;
    let ok = false;
    let errorCode = '';
    let aborted = false;
    loadingIndicator.begin();

    try {
      let response;
      try {
        response = await fetch(this.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
          signal,
        });
      } catch (error) {
        if (error?.name === 'AbortError') {
          aborted = true;
          throw error;
        }
        throw new ApiError('Сервер недоступен. Проверьте соединение.', {
          code: 'network_error',
          action,
        });
      }

      status = response.status;
      let payload;
      try {
        payload = await response.json();
      } catch {
        const error = new ApiError(response.status === 401 ? 'Сессия завершена. Войдите снова.' : 'Сервер вернул некорректный ответ.', {
          status: response.status,
          code: response.status === 401 ? 'authentication_required' : 'invalid_response',
          action,
        });
        if (this.isAuthenticationLost(response.status, error.code)) this.onAuthenticationLost(error);
        throw error;
      }

      if (!response.ok || payload?.success === false) {
        const source = payload?.error || {};
        const error = new ApiError(source.message || `Ошибка HTTP ${response.status}`, {
          status: response.status,
          code: source.code || 'api_error',
          details: source.details || null,
          action,
        });
        if (this.isAuthenticationLost(response.status, error.code)) this.onAuthenticationLost(error);
        throw error;
      }

      if (payload?.csrf_token) this.setCsrfToken(payload.csrf_token);
      ok = true;
      return payload;
    } catch (error) {
      errorCode = error?.code || error?.name || 'request_failed';
      throw error;
    } finally {
      loadingIndicator.end();
      const finished = globalThis.performance?.now?.() ?? Date.now();
      try {
        this.onRequestComplete({
          action,
          ok,
          status,
          errorCode,
          aborted,
          durationMs: Math.max(0, Math.round(finished - started)),
        });
      } catch {}
    }
  }
}
